import React, { useEffect, useState } from "react";
import Column from "@amzn/meridian/column";
import Row from "@amzn/meridian/row";
import Alert from "@amzn/meridian/alert";
import Text from "@amzn/meridian/text";
import Link from "@amzn/meridian/link";
import Divider from "@amzn/meridian/divider";
import Button from "@amzn/meridian/button";
import Select, { SelectOption } from "@amzn/meridian/select";
import Tag from "@amzn/meridian/tag";
import { useNavigate, useParams } from "react-router-dom";
import Breadcrumb, { BreadcrumbGroup } from "@amzn/meridian/breadcrumb";
import Icon from "@amzn/meridian/icon";
import chevronLeftLargeToken from "@amzn/meridian-tokens/base/icon/chevron-left-large";
import chevronRightLargeToken from "@amzn/meridian-tokens/base/icon/chevron-right-large";
import exportSmallToken from "@amzn/meridian-tokens/base/icon/export-small";
import editToken from "@amzn/meridian-tokens/base/icon/edit";
import Table, { TableCell, TableRow } from "@amzn/meridian/table";
import { useAppDispatch, useAppSelector } from "src/store/store";
import {
  Field,
  getSiteDetails,
  resetGetSiteDetailsLoading,
  setSelectedModelId,
} from "src/store/modelManagementSlice";
import {
  addEvent,
  AuditField,
  AuditResult,
  AuditStatus,
  getAuditData,
  getAuditDataListView,
  getAuditJobById,
  resetAuditReviewPage,
  setFieldConfirmedAnswer,
  updateAuditData,
} from "src/store/auditSlice";
import meridianColors from "@amzn/meridian-tokens/base/color";
import { getClarification } from "src/store/dataExtractionSlice";
import "./auditReviewView.scss";
import { createSearchRegExp, linkClickHandler } from "src/helpers";
import {
  findNextUnreviewedCase,
  highlightText,
  isAnswerInadequate,
  isAnswerMatch,
  isAustinAnswerNoObjectInvolved,
  isConfirmedAnswerValid,
  isFieldMultiSelected,
  moveToTextTop,
  removeHighlightedText,
} from "src/components/pages/audit/helpers";
import Loader from "@amzn/meridian/loader";
import { createToast } from "src/store/toastsSlice";
import { TOAST_TIMEOUT } from "src/config/Toast";
import { INADEQUATE_INFORMATION, NO_OBJECT_INVOLVED, OTHER } from "src/constants";
import AuditAnswerCells from "src/components/pages/audit/auditReview/auditAnswerCells/auditAnswerCells";
import FullOptionsList from "src/components/pages/audit/auditReview/fullOptionList/fullOptionList";
import { getSourceDataIncident, resetSourceData } from "src/store/sourceDataSlice";

const AuditReviewView = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const { token, alias } = useAppSelector((state) => state.user);
  const {
    auditDataListView,
    selectedAuditData,
    selectedAuditJob,
    getListViewLoading,
    getAuditDataLoading,
    getAuditJobLoading,
    updateAuditDataLoading,
    isBlind,
  } = useAppSelector((state) => state.auditSlice);
  const { incident, getIncidentLoading: getIncidentDataLoading } = useAppSelector(
    (state) => state.sourceDataSlice,
  );
  const { selectedModel, getSiteDetailsLoading } = useAppSelector((state) => state.modelSlice);

  const { auditWorkflowId, incidentId } = useParams();

  const [searchQuery, setSearchQuery] = useState("");
  const searchRegExp = createSearchRegExp(searchQuery);
  const [shouldShowError, setShouldShowError] = useState(false);
  const [doesClarificationExist, setDoesClarificationExist] = useState(true);
  const [auditListIndex, setAuditListIndex] = useState(0);
  const [randomizedFieldOutputOrders, setRandomizedFieldOutputOrders] = useState<boolean[]>([]);
  const [isEditMode, setIsEditMode] = useState(false);

  useEffect(() => {
    if (token && auditWorkflowId && incidentId) {
      dispatch(
        getAuditData({
          auditWorkflowId: auditWorkflowId,
          incidentId: incidentId,
        }),
      );
    }
  }, [auditWorkflowId, incidentId, token]);

  useEffect(() => {
    if (token && auditWorkflowId && getAuditJobLoading === "idle") {
      dispatch(getAuditJobById(auditWorkflowId));
    }
  }, [auditWorkflowId, token, getAuditJobLoading]);

  useEffect(() => {
    if (token && auditWorkflowId && getListViewLoading === "idle") {
      dispatch(getAuditDataListView(auditWorkflowId));
    }
  }, [auditWorkflowId, token, getListViewLoading]);

  useEffect(() => {
    if (token && incidentId) {
      dispatch(resetAuditReviewPage());
      dispatch(getSourceDataIncident(incidentId));
    }
  }, [incidentId, token]);

  useEffect(() => {
    if (
      selectedModel.id != "" &&
      getIncidentDataLoading === "fulfilled" &&
      getSiteDetailsLoading === "idle" &&
      incident?.site
    ) {
      dispatch(getSiteDetails(incident.site));
    }
  }, [getIncidentDataLoading, selectedModel]);

  useEffect(() => {
    if (getAuditDataLoading === "fulfilled") {
      dispatch(setSelectedModelId(selectedAuditData.modelId));
      dispatch(
        addEvent({
          action: "audit_start",
          timestamp: new Date().toISOString(),
          auditor: alias,
        }),
      );
      setRandomizedFieldOutputOrders(selectedAuditData.auditFields.map(() => Math.random() < 0.5));
      // Set edit mode: true for unreviewed, false for reviewed
      setIsEditMode(selectedAuditData.status === AuditStatus.UNREVIEWED);
    } else if (getAuditDataLoading === "rejected") {
      navigate(`/audit/${auditWorkflowId}`);
    }
  }, [getAuditDataLoading]);

  useEffect(() => {
    if (getListViewLoading === "fulfilled" && incidentId) {
      setAuditListIndex(
        auditDataListView.findIndex(
          (auditDataListView) => auditDataListView.incidentId === incidentId,
        ),
      );
    } else if (getListViewLoading === "rejected") {
      navigate(`/audit/${auditWorkflowId}`);
    }
  }, [getListViewLoading, incidentId]);

  const resetAuditState = () => {
    dispatch(resetAuditReviewPage());
    dispatch(resetGetSiteDetailsLoading());
    dispatch(resetSourceData());
  };

  useEffect(() => {
    if (updateAuditDataLoading === "fulfilled" && incidentId) {
      resetAuditState();
      const next = findNextUnreviewedCase(auditDataListView, incidentId);
      if (next) {
        navigate(`/audit/${auditWorkflowId}/${next.incidentId}`);
      } else {
        navigate(`/audit/${auditWorkflowId}`);
        dispatch(
          createToast({
            type: "success",
            message: "All incidents have finished reviewed, redirect to list view page...",
            timeout: TOAST_TIMEOUT,
          }),
        );
      }
    }
  }, [updateAuditDataLoading, incidentId]);

  const getLabel = (
    auditField: AuditField,
    type: AuditResult,
    matching: boolean,
    isBlind: boolean,
  ): string => {
    // Check if this field has smart assistant data
    const smartAssistantField = selectedAuditData.smartAssistantData?.fieldData.find(
      (field) => field.fieldName === auditField.fieldName,
    );
    switch (type) {
      case AuditResult.BOTH_CORRECT:
        return matching
          ? `${getAnswersSeparatebyComma(auditField.llmAnswer)} ${isBlind ? "" : "-- Austin/LLM"}`
          : AuditResult.BOTH_CORRECT;
      case AuditResult.BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT:
        return matching
          ? `${getAnswersSeparatebyComma(smartAssistantField?.suggestedValues.join(", ") || "")} ${isBlind ? "" : "-- Austin/EHS"}`
          : AuditResult.BOTH_CORRECT;
      case AuditResult.EHS_SMART_ASSISTANT_CORRECT:
        return `${getAnswersSeparatebyComma(smartAssistantField?.suggestedValues.join(", ") || "")} ${isBlind ? "" : "-- EHS"}`;
      case AuditResult.LLM_CORRECT:
        return `${getAnswersSeparatebyComma(auditField.llmAnswer)} ${isBlind ? "" : "-- LLM"}`;
      case AuditResult.INADEQUATE:
        return INADEQUATE_INFORMATION;
      case AuditResult.HUMAN_CORRECT:
        return isAustinAnswerNoObjectInvolved(auditField)
          ? `${NO_OBJECT_INVOLVED} ${isBlind ? "" : "-- Austin"}`
          : `${getAnswersSeparatebyComma(auditField.austinAnswer)} ${isBlind ? "" : "-- Austin"}`;
      default:
        return OTHER;
    }
  };

  // Function to render result labels with tags for better visual appearance
  const renderResultLabel = (
    auditField: AuditField,
    type: AuditResult,
    matching: boolean,
    isBlind: boolean,
  ): JSX.Element => {
    const smartAssistantField = selectedAuditData.smartAssistantData?.fieldData.find(
      (field) => field.fieldName === auditField.fieldName,
    );

    const createLabelWithTag = (
      answer: string,
      tagText: string,
      tagType: "neutral" | "success" | "warning" | "error" = "neutral",
    ) => (
      <Column spacing="100" alignmentHorizontal="start">
        <Text>{answer}</Text>
        {!isBlind && <Tag type={tagType}>{tagText}</Tag>}
      </Column>
    );

    switch (type) {
      case AuditResult.BOTH_CORRECT:
        // Show the actual answer with Austin/LLM tag
        return createLabelWithTag(
          getAnswersSeparatebyComma(auditField.llmAnswer),
          "Austin/LLM",
          "success",
        );
      case AuditResult.BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT:
        // Show the actual answer with Austin/EHS tag
        return createLabelWithTag(
          getAnswersSeparatebyComma(smartAssistantField?.suggestedValues.join(", ") || ""),
          "Austin/EHS",
          "success",
        );
      case AuditResult.EHS_SMART_ASSISTANT_CORRECT:
        return createLabelWithTag(
          getAnswersSeparatebyComma(smartAssistantField?.suggestedValues.join(", ") || ""),
          "EHS",
          "warning",
        );
      case AuditResult.LLM_CORRECT:
        return createLabelWithTag(
          getAnswersSeparatebyComma(auditField.llmAnswer),
          "LLM",
          "warning",
        );
      case AuditResult.INADEQUATE:
        return <Text>{INADEQUATE_INFORMATION}</Text>;
      case AuditResult.HUMAN_CORRECT:
        return isAustinAnswerNoObjectInvolved(auditField)
          ? createLabelWithTag(NO_OBJECT_INVOLVED, "Austin", "warning")
          : createLabelWithTag(
              getAnswersSeparatebyComma(auditField.austinAnswer),
              "Austin",
              "warning",
            );
      case AuditResult.NEITHER:
        // Show confirmed answer with error tag
        const confirmedAnswer = Array.isArray(auditField.confirmedAnswer)
          ? auditField.confirmedAnswer.join(", ")
          : auditField.confirmedAnswer || "";
        return createLabelWithTag(
          getAnswersSeparatebyComma(confirmedAnswer),
          "Both AI and Human Defect",
          "error",
        );
      default:
        return <Text>{OTHER}</Text>;
    }
  };

  const getConfirmedAnswer = (auditField: AuditField, type: AuditResult, many: boolean) => {
    const smartAssistantField = selectedAuditData.smartAssistantData?.fieldData.find(
      (field) => field.fieldName === auditField.fieldName,
    );
    if (
      type === AuditResult.HUMAN_CORRECT ||
      type === AuditResult.BOTH_CORRECT ||
      type === AuditResult.BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT
    ) {
      return auditField.austinAnswer;
    } else if (type === AuditResult.EHS_SMART_ASSISTANT_CORRECT) {
      return smartAssistantField ? smartAssistantField.suggestedValues.join("|") : "";
    } else if (type === AuditResult.LLM_CORRECT) {
      return auditField.llmAnswer;
    } else if (type === AuditResult.INADEQUATE) {
      return INADEQUATE_INFORMATION;
    } else if (type === AuditResult.NEITHER) {
      return many ? [] : AuditResult.NEITHER;
    }
  };

  const showExtraOptions = (auditField: AuditField) => {
    return isEditMode && auditField.auditResult === AuditResult.NEITHER;
  };

  const getAnswersSeparatebyComma = (answer: string) => {
    if (answer.includes("|")) {
      return answer.split("|").join(", ");
    }
    return answer;
  };

  const saveAuditDataButton = () => {
    {
      return updateAuditDataLoading === "pending" ? (
        <Button size="large">
          <Loader size={"small"} />
        </Button>
      ) : (
        <Button
          size="large"
          onClick={() => {
            if (
              !selectedAuditData.auditFields.some(
                (auditField) => !isConfirmedAnswerValid(auditField),
              )
            ) {
              dispatch(
                updateAuditData({
                  auditData: selectedAuditData,
                  reviewer: alias,
                }),
              );
              setShouldShowError(false);
            } else {
              setShouldShowError(true);
            }
          }}
          data-cy="save-and-next__button"
        >
          Save & Next
        </Button>
      );
    }
  };

  return (
    <Column spacingInset={"400"} spacing={"none"}>
      <BreadcrumbGroup>
        <Breadcrumb onClick={() => navigate(`/audit`)}>Audit workflow</Breadcrumb>
        <Breadcrumb onClick={() => navigate(`/audit/${auditWorkflowId}`)}>
          {selectedAuditJob.modelName} - Processed
        </Breadcrumb>
        <Breadcrumb>{selectedAuditData.caseId}</Breadcrumb>
      </BreadcrumbGroup>
      <Row widths={["80%", "20%"]}>
        <Row spacing={"400"}>
          <div
            onClick={() => {
              if (auditDataListView[auditListIndex - 1]) {
                resetAuditState();
                navigate(
                  `/audit/${auditWorkflowId}/${auditDataListView[auditListIndex - 1].incidentId}`,
                );
              } else {
                navigate(`/audit/${auditWorkflowId}`);
              }
            }}
          >
            <Column spacingInset={"300"} className="icon">
              <Icon tokens={chevronLeftLargeToken} />
            </Column>
          </div>
          <div
            onClick={() => {
              if (auditDataListView[auditListIndex + 1]) {
                resetAuditState();
                navigate(
                  `/audit/${auditWorkflowId}/${auditDataListView[auditListIndex + 1].incidentId}`,
                );
              } else {
                navigate(`/audit/${auditWorkflowId}`);
              }
            }}
          >
            <Column spacingInset={"300"} className="icon">
              <Icon tokens={chevronRightLargeToken} />
            </Column>
          </div>
          <Text type="h500">
            <p className="weight">Case ID: {selectedAuditData.caseId}</p>
          </Text>
          {incident?.suborg && (
            <Text type="h100">
              <p className="weight">Sub Org: {incident.suborg}</p>
            </Text>
          )}
          <Link
            onClick={() => {
              dispatch(
                addEvent({
                  action: "click_link_to_austin_case",
                  timestamp: new Date().toISOString(),
                  auditor: alias,
                }),
              );
              linkClickHandler(
                incident?.incidentUrl ||
                  `https://na.ehs-amazon.com/incidents/incident/${selectedAuditData.incidentId}/summary`,
              );
            }}
          >
            Link to Austin Case
          </Link>
          <Icon tokens={exportSmallToken} />
          <Text>
            <p className="weight">Status: {selectedAuditData.status}</p>
          </Text>
          {selectedAuditData.status === AuditStatus.REVIEWED && (
            <Text>
              <p className="weight">Reviewed by: {selectedAuditData.reviewer}</p>
            </Text>
          )}
        </Row>
        <Row alignmentHorizontal="end" widths={["60%", "40%"]}>
          <Text type="h500">
            {auditDataListView.length > 0 && (
              <p className="weight">{`${auditListIndex + 1} / ${auditDataListView.length}`}</p>
            )}
          </Text>
          <Row widths={"fill"} alignmentHorizontal="center" spacing="200">
            {selectedAuditData.status === AuditStatus.REVIEWED && !isEditMode && (
              <Button type="tertiary" size="medium" onClick={() => setIsEditMode(true)}>
                <Icon tokens={editToken} />
                Edit
              </Button>
            )}
            {(isEditMode || selectedAuditData.status === AuditStatus.UNREVIEWED) &&
              saveAuditDataButton()}
          </Row>
        </Row>
      </Row>
      <Divider></Divider>
      <Row widths={["35%", "fit", "65%"]} alignmentVertical="top" spacing={"none"}>
        <Column height="76vh" overflowY="auto" spacingInset={"0 400 0 400"}>
          <Text
            id="incident-description-container"
            fontFamily="amazonEmber"
            type="b500"
            color="primary"
          >
            {!doesClarificationExist && (
              <Column spacingInset={"500 0 0 0"}>
                <Alert type="warning">No phrase identified</Alert>
              </Column>
            )}
            <p id="incident-description" className="paragraph-style">
              {selectedAuditData.incidentDescription}
            </p>
          </Text>
        </Column>
        <Column className="audit-review-split-line"></Column>
        <Column height="76vh" overflowY="auto">
          <Table showDividers={true} fixHeaderRows={true} headerRows={1}>
            <TableRow backgroundColor={meridianColors.colorGray50}>
              <TableCell width={"1%"}></TableCell>
              <TableCell width={"20%"}>
                <Text type="h200">Fields</Text>
              </TableCell>
              <TableCell width={"20%"}>
                <Text type="h200">{isBlind ? "Source 1" : "Austin Output"}</Text>
              </TableCell>
              <TableCell width={"20%"}>
                <Text type="h200">{isBlind ? "Source 2" : "AI Output"}</Text>
              </TableCell>
              <TableCell width={"20%"} alignmentHorizontal={"start"}>
                <Text type="h200">Review Decision</Text>
              </TableCell>
              <TableCell width={"20%"}></TableCell>
            </TableRow>
            {selectedAuditData.auditFields.map((auditField, index) => {
              const smartAssistantField = selectedAuditData.smartAssistantData?.fieldData.find(
                (field) => field.fieldName === auditField.fieldName,
              );
              const showSmartAssistantAnswer = smartAssistantField?.suggestedValues;
              const aiAnswer = showSmartAssistantAnswer
                ? smartAssistantField.suggestedValues.join(", ")
                : auditField.llmAnswer;

              const aiResultCorrect = showSmartAssistantAnswer
                ? AuditResult.EHS_SMART_ASSISTANT_CORRECT
                : AuditResult.LLM_CORRECT;
              const austinAIBothCorrect = showSmartAssistantAnswer
                ? AuditResult.BOTH_AUSTIN_AND_EHS_SMART_ASSISTANT_CORRECT
                : AuditResult.BOTH_CORRECT;
              const isAustinAIMatch = isAnswerMatch(
                auditField.austinAnswer,
                aiAnswer,
                isFieldMultiSelected(auditField, selectedModel),
              );
              return (
                <TableRow
                  highlightOnHover
                  onMouseEnter={() => {
                    // set timeout to solve the UI mismatch caused by
                    // clarificationExist variable compete when hover mouse enter and leave
                    setTimeout(() => {
                      if (auditField.llmClarification.length > 0) {
                        highlightText(getClarification(auditField.llmClarification));
                      } else {
                        moveToTextTop();
                        setDoesClarificationExist(false);
                      }
                    }, 100);
                  }}
                  onMouseLeave={() => {
                    setDoesClarificationExist(true);
                    removeHighlightedText();
                  }}
                >
                  <TableCell width={"1%"}></TableCell>
                  <TableCell width={"20%"}>{auditField.fieldName}</TableCell>
                  <AuditAnswerCells
                    isBlind={isBlind}
                    randomizedOrder={randomizedFieldOutputOrders[index]}
                    auditField={auditField}
                    index={index}
                  />
                  <TableCell
                    width={"20%"}
                    alignmentHorizontal={"start"}
                    alignmentVertical={"bottom"}
                  >
                    <Row>
                      {isEditMode ? (
                        <Select
                          width={"200px"}
                          value={auditField.auditResult}
                          onChange={(value) => {
                            dispatch(
                              setFieldConfirmedAnswer({
                                fieldIndex: index,
                                confirmedAnswer: getConfirmedAnswer(
                                  auditField,
                                  value,
                                  isFieldMultiSelected(auditField, selectedModel),
                                ),
                                auditResult: value,
                                alias: alias,
                              }),
                            );
                          }}
                          placeholder={"Choose output..."}
                          size="medium"
                          error={shouldShowError && !isConfirmedAnswerValid(auditField)}
                          errorMessage={
                            shouldShowError &&
                            !isConfirmedAnswerValid(auditField) &&
                            "please select a answer"
                          }
                          data-cy={`audit-data-review-view-${index}-row__select`}
                        >
                          {!isAustinAIMatch && !isAnswerInadequate(aiAnswer) && (
                            <SelectOption
                              key={aiResultCorrect}
                              value={aiResultCorrect}
                              label={getLabel(
                                auditField,
                                aiResultCorrect,
                                isAustinAIMatch,
                                isBlind,
                              )}
                            />
                          )}
                          {!isAustinAIMatch &&
                            (!isAnswerInadequate(auditField.austinAnswer) ||
                              isAustinAnswerNoObjectInvolved(auditField)) && (
                              <SelectOption
                                key={AuditResult.HUMAN_CORRECT}
                                value={AuditResult.HUMAN_CORRECT}
                                label={getLabel(
                                  auditField,
                                  AuditResult.HUMAN_CORRECT,
                                  isAustinAIMatch,
                                  isBlind,
                                )}
                              />
                            )}
                          {!isAnswerInadequate(aiAnswer) &&
                            (isAustinAnswerNoObjectInvolved(auditField) ||
                              !isAnswerInadequate(auditField.austinAnswer)) && (
                              <SelectOption
                                key={austinAIBothCorrect}
                                value={austinAIBothCorrect}
                                label={getLabel(
                                  auditField,
                                  austinAIBothCorrect,
                                  isAustinAIMatch,
                                  isBlind,
                                )}
                              />
                            )}
                          <SelectOption
                            key={AuditResult.INADEQUATE}
                            value={AuditResult.INADEQUATE}
                            label={getLabel(
                              auditField,
                              AuditResult.INADEQUATE,
                              isAnswerInadequate(auditField.llmAnswer),
                              isBlind,
                            )}
                          />
                          <SelectOption
                            key={AuditResult.NEITHER}
                            value={AuditResult.NEITHER}
                            label={getLabel(auditField, AuditResult.NEITHER, false, isBlind)}
                          />
                        </Select>
                      ) : (
                        <div>
                          {auditField.auditResult &&
                            renderResultLabel(
                              auditField,
                              auditField.auditResult,
                              isAustinAIMatch,
                              isBlind,
                            )}
                        </div>
                      )}
                    </Row>
                  </TableCell>
                  <TableCell
                    width={"20%"}
                    alignmentHorizontal={"center"}
                    alignmentVertical={"bottom"}
                  >
                    <Row>
                      {selectedModel.id !== "" && showExtraOptions(auditField) && (
                        <FullOptionsList
                          auditField={auditField}
                          fieldIndex={index}
                          shouldShowError={shouldShowError}
                        />
                      )}
                    </Row>
                  </TableCell>
                </TableRow>
              );
            })}
          </Table>
        </Column>
      </Row>
    </Column>
  );
};

export default AuditReviewView;
